import type { PromptContent, PromptOverride, ReviewInput, ReviewPromptConfig } from "./agent.js";

export interface ResolvedPrompts {
  root: string;
  review: string;
}

export function buildRootPrompt(): string {
  return `You are Revuiw, an autonomous pull request UI reviewer. Act as a rigorous product engineer validating changed behavior in the running application.

Use repository evidence and direct browser observation. Be systematic, skeptical, and concrete. Do not modify the repository. Do not invent findings, expected behavior, coverage, or reproduction steps. Distinguish product defects from review-infrastructure failures. Report only issues that another engineer can reproduce and act on.`;
}

export function buildReviewPrompt(input: ReviewInput): string {
  const plan = input.plan
    ? `\n\nA planning agent inspected the change before this review. Use its plan as prioritization guidance, but verify every claim yourself:\n${JSON.stringify(input.plan, null, 2)}`
    : "";
  return `Review the current pull request against the running application.

Application: ${input.baseUrl.toString()}
Pull request diff: ${input.pullRequestDiffPath}
Evidence directory: ${input.evidenceDirectory}

Read the diff and relevant frontend source before reviewing. Use Playwright MCP for all browser interaction and discover the application through its visible UI. Prioritize affected areas, then exercise primary navigation, desktop and mobile layouts, keyboard navigation, forms, dialogs, menus, validation, reachable UI states, console errors, failed requests, overflow, clipping, accessible names, labels, roles, and interaction semantics.

Only report concrete, reproducible problems, not subjective design preferences. Capture a screenshot for every visual, responsive, or accessibility finding when possible. Return the requested structured result.${plan}`;
}

export async function resolvePrompts(
  input: ReviewInput,
  config: ReviewPromptConfig | undefined = input.prompt,
): Promise<ResolvedPrompts> {
  const root = await resolvePromptOverride(buildRootPrompt(), config?.root, input);
  let review = await resolvePromptOverride(buildReviewPrompt(input), config?.review, input);
  const notes = [
    ...(config?.note === undefined ? [] : [config.note]),
    ...(config?.notes ?? []),
  ];

  if (notes.length) {
    const resolved = await Promise.all(notes.map((note) => resolveContent(note, input, review)));
    review = `${review}\n\nAdditional review notes:\n${resolved.map((note) => `- ${note}`).join("\n")}`;
  }
  return { root, review };
}

export async function resolvePromptOverride(
  currentPrompt: string,
  override: PromptContent | PromptOverride | undefined,
  input: ReviewInput,
): Promise<string> {
  if (override === undefined) return currentPrompt;
  const normalized = typeof override === "object" ? override : { mode: "append" as const, content: override };
  const content = await resolveContent(normalized.content, input, currentPrompt);
  return normalized.mode === "replace" ? content : `${currentPrompt}\n\n${content}`;
}

async function resolveContent(content: PromptContent, input: ReviewInput, currentPrompt: string): Promise<string> {
  const resolved = typeof content === "function" ? await content(input, currentPrompt) : content;
  const trimmed = resolved.trim();
  if (!trimmed) throw new Error("Prompt customization returned empty content");
  return trimmed;
}
